import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../users/userTypes';
import type {
  CreateRoleRequest,
  PermissionCatalogModule,
  RoleDetail,
  RoleListItem,
  SetRolePermissionsRequest,
  UpdateRoleRequest,
} from './roleTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Facets are sent whenever the key is present, empty string included — an empty value is the
    reader saying "none of these", which matches no row. A truthiness check here would drop it
    and the server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const rolesApi = {
  /** Soft-deletes every record in the module at once; resolves with how many were deleted. */
  clear: () => api<number>('/api/roles/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<RoleListItem>>(`/api/roles${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<RoleDetail>(`/api/roles/${id}`, { signal }),

  /** The permission catalog the role editor renders, grouped by module. */
  permissionCatalog: (signal?: AbortSignal) =>
    api<PermissionCatalogModule[]>('/api/roles/permission-catalog', { signal }),

  create: (request: CreateRoleRequest) =>
    api<RoleDetail>('/api/roles', { method: 'POST', body: request }),

  update: (id: string, request: UpdateRoleRequest) =>
    api<RoleDetail>(`/api/roles/${id}`, { method: 'PUT', body: request }),

  setPermissions: (id: string, request: SetRolePermissionsRequest) =>
    api<RoleDetail>(`/api/roles/${id}/permissions`, { method: 'PUT', body: request }),

  /** Soft delete. Refused while the role still has members. */
  deactivate: (id: string) => api<void>(`/api/roles/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/roles/${id}/activate`, { method: 'POST' }),
};
